import { useCallback, useEffect, useRef, useState } from "react";

import { Button, StopSVG, VolumeUpSVG } from "@newsela/angelou";
import { withTrackEvent } from "@newsela/snitch";
import PropTypes from "prop-types";

import { ES_MX_LANGUAGE_CODE, LANG_ES } from "@/constants/Language/Language";
import requestAudioFile from "@/view/ReadAloudControls/useReadAloudAudio/utils/requestAudioFile";
import requestTextToSpeechAudioFile from "@/view/ReadAloudControls/useReadAloudAudio/utils/requestTextToSpeechAudioFile";
import { rootElementStyles } from "./StandaloneReadAloudButton.style";

const propTypes = {
  readAloudData: PropTypes.object,
  iconCss: PropTypes.object,
  buttonOverrideCss: PropTypes.object,
  language: PropTypes.string,
  text: PropTypes.string,
  eventProperties: PropTypes.object,
  powerWord: PropTypes.string,
  dataProps: PropTypes.object,
};
const ButtonWithTrackEvent = withTrackEvent(Button);

const StandaloneReadAloudButton = ({
  text = "",
  language,
  iconCss = {},
  buttonOverrideCss = {},
  eventProperties = {},
  powerWord,
  dataProps = {},
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const readAloudRef = useRef();
  const audioUrlRef = useRef("");
  const CurrentIcon = isPlaying ? StopSVG : VolumeUpSVG;

  const fetchAudioUrl = useCallback(async () => {
    if (audioUrlRef.current) return audioUrlRef.current;

    if (language === LANG_ES) {
      const { data } = await requestTextToSpeechAudioFile(
        text,
        ES_MX_LANGUAGE_CODE,
      );
      audioUrlRef.current = data?.download_url;
    } else {
      const { data } = await requestAudioFile(text);
      audioUrlRef.current = data?.download_url;
    }
    return audioUrlRef.current;
  }, [text, language]);

  const playReadAloud = async () => {
    const url = await fetchAudioUrl();
    if (readAloudRef.current && url) {
      readAloudRef.current.src = url;
      readAloudRef.current.play();
      setIsPlaying(true);
    }
  };

  const stopReadAloud = () => {
    readAloudRef.current?.pause();
    readAloudRef.current.currentTime = 0;
    setIsPlaying(false);
  };

  const onIconClick = !isPlaying ? playReadAloud : stopReadAloud;

  const onAudioEnded = () => {
    setIsPlaying(false);
  };

  useEffect(() => {
    fetchAudioUrl().then((url) => {
      if (readAloudRef.current && url) {
        readAloudRef.current.src = url;
      }
    });
  }, [fetchAudioUrl]);

  return (
    <span css={rootElementStyles}>
      <ButtonWithTrackEvent
        ariaProps={{
          "aria-label": isPlaying
            ? `Stop ${powerWord} definition`
            : `Play ${powerWord} definition`,
        }}
        dataProps={dataProps}
        __cssFor={{
          root: buttonOverrideCss,
          Icon: { root: iconCss },
        }}
        icon={{
          SvgComponent: CurrentIcon,
          alignment: Button.iconAlignments.right,
        }}
        flavor={Button.flavors.tertiary}
        onClick={onIconClick}
        {...eventProperties}
      />
      <audio onEnded={onAudioEnded} ref={readAloudRef} />
    </span>
  );
};

StandaloneReadAloudButton.propTypes = propTypes;

export default StandaloneReadAloudButton;
